import type { CreatorPlatform } from '../shared/platforms';

// The creator dashboard's own data shapes. services/dashboard.mapper.ts maps
// the creators API's responses onto them, so components don't depend on the
// API's wire format.

export const LOBS = ['flights', 'hotels'] as const;
export type Lob = (typeof LOBS)[number];

/** The signed-in creator's programme record, shared by the dashboard tabs.
 *  Their name and photo come from the ixigo login's user info instead. */
export interface CreatorProfile {
	/** The creator's custom code, e.g. RIYA10. Locked once approved. */
	readonly code: string;
	/** ISO 8601 timestamp of when the code went live; null until approved. */
	readonly liveSince: string | null;
}

/** All amounts are whole rupees. */
export interface CreatorEarnings {
	/** Booked with the code; the trip hasn't happened yet. */
	readonly pending: number;
	/** Trip completed and not yet withdrawn — the withdrawable balance. */
	readonly confirmed: number;
	/** Already withdrawn to ixigo money / ixigo money max. */
	readonly paid: number;
	/** Bookings made with the code over its lifetime. */
	readonly bookings: number;
}

/** Home: the programme's terms (fixed in ./programme.ts) and the earnings. */
export interface CreatorHome {
	readonly offer: {
		/** Discount followers get with the code, as a whole percent. */
		readonly discountPercent: number;
		/** Largest discount per booking, in rupees. */
		readonly maxDiscount: Readonly<Record<Lob, number>>;
	};
	readonly rates: Readonly<
		Record<Lob, { readonly percent: number; readonly capPerBooking: number }>
	>;
	readonly earnings: CreatorEarnings;
	/** Smallest confirmed balance that can be withdrawn, in rupees. */
	readonly minPayout: number;
}

export type PayoutDestination = 'ixigo_money' | 'ixigo_money_max';

/** Why the Withdraw button is locked. `verification_pending`: a WhatsApp /
 *  Telegram channel is waiting on ops before payouts open. */
export const PAYOUT_BLOCKED_REASONS = [
	'below_minimum',
	'verification_pending',
] as const;
export type PayoutBlockedReason = (typeof PAYOUT_BLOCKED_REASONS)[number];

/** A WhatsApp / Telegram channel whose verification is holding up payouts. */
export interface PendingChannel {
	readonly platform: CreatorPlatform;
	/** "Rail Diaries", its @handle, or its link while it has neither. */
	readonly name: string;
	/** Members. */
	readonly audience: number;
}

/** What the creators API's earnings summary gives the dashboard: the code in
 *  the header and sidebar, Home, and the top of Payout. */
export interface CreatorSummary {
	readonly profile: CreatorProfile;
	readonly home: CreatorHome;
	readonly payout: CreatorPayout;
}

/** Everything the Payout tab's top half and the Withdraw sheet show. */
export interface CreatorPayout {
	readonly earnings: CreatorEarnings;
	/** Smallest confirmed balance that can be withdrawn, in rupees. */
	readonly minPayout: number;
	/** Extra paid on withdrawals to ixigo money, as a whole percent. */
	readonly ixigoMoneyBonusPercent: number;
	/** ixigo money terms (expiry, usage limits); null until they're written. */
	readonly bonusTermsUrl: string | null;
	readonly payout: {
		readonly canWithdraw: boolean;
		readonly blockedReason: PayoutBlockedReason | null;
		/** The confirmed balance a withdrawal pays out; Withdraw sends it as
		 *  the amount. */
		readonly balance: number;
		/** With `verification_pending`: the channels ops still have to verify.
		 *  Empty otherwise. */
		readonly pendingChannels: readonly PendingChannel[];
		/** What the whole confirmed balance pays out at each destination, bonus
		 *  included. Rounded by the backend, so the UI never does the maths. */
		readonly quote: {
			readonly ixigoMoney: number;
			readonly ixigoMoneyMax: number;
		};
	};
	/** Earlier withdrawals, newest first. */
	readonly previousPayouts: readonly PastPayout[];
}

export const COMMISSION_STATUSES = [
	'pending',
	'confirmed',
	'paid',
	'reversed',
] as const;
export type CommissionStatus = (typeof COMMISSION_STATUSES)[number];

/** One booking made with the creator's code, as a row in Payout's History. */
export interface Commission {
	/** The booking id; a stand-in while the booking's details are syncing. */
	readonly id: string;
	readonly type: Lob;
	/** "Mumbai → Goa", "Candolim, 2 nights". */
	readonly title: string;
	/** ISO dates: departure (and return) for flights; check-in and check-out
	 *  for hotels. Null while the booking's details are still syncing;
	 *  `travelEnd` is also null for a one-way flight. */
	readonly travelStart: string | null;
	readonly travelEnd: string | null;
	/** `reversed`: the booking was cancelled and the commission dropped. */
	readonly status: CommissionStatus;
	/** Whole rupees. */
	readonly commission: number;
}

/** A page of History, newest first. `nextCursor` is null on the last page. */
export interface CommissionsPage {
	readonly items: readonly Commission[];
	readonly nextCursor: string | null;
}

export interface WithdrawalRequest {
	readonly destination: PayoutDestination;
	/** The confirmed balance the creator saw; MVP withdraws all of it. */
	readonly amount: number;
	/** Idempotency key, so a double tap can't pay out twice. */
	readonly requestId: string;
}

export const WITHDRAWAL_STATUSES = ['success', 'processing', 'failed'] as const;
export type WithdrawalStatus = (typeof WITHDRAWAL_STATUSES)[number];

export interface WithdrawalResult {
	readonly status: WithdrawalStatus;
	/** What landed, bonus included (₹1,240 → ₹1,364 as ixigo money). */
	readonly creditedAmount: number;
	readonly transactionId: string;
}

/** A withdrawal made earlier, as Payout's Previous payouts lists it. Amounts
 *  in rupees. */
export interface PastPayout {
	readonly id: string;
	readonly destination: PayoutDestination;
	readonly status: WithdrawalStatus;
	/** The confirmed balance it paid out. */
	readonly withdrawn: number;
	/** The ixigo money bonus on top; 0 for ixigo money max. */
	readonly bonus: number;
	/** What landed, bonus included; nothing when it failed. */
	readonly credited: number;
	/** ISO timestamp of when it was made. */
	readonly createdAt: string;
}

/** Analytics can show every booking or one booking type. */
export type AnalyticsFilter = 'all' | Lob;

/** The periods Analytics offers, each ending today. */
export const ANALYTICS_PERIODS = ['30d', '60d', '90d', '6m', '1y'] as const;
export type AnalyticsPeriod = (typeof ANALYTICS_PERIODS)[number];

/** A period's first and last day, inclusive, as IST dates (yyyy-MM-dd). */
export interface AnalyticsRange {
	readonly from: string;
	readonly to: string;
}

/** What Analytics covers: a preset ending today, or dates the creator picked. */
export type AnalyticsSelection =
	| { readonly period: AnalyticsPeriod }
	| { readonly period: 'custom'; readonly range: AnalyticsRange };

/** One booking type's numbers for the analytics period. Amounts in rupees. */
export interface AnalyticsBreakdown {
	/** Pending + confirmed + paid commission on bookings made in the period. */
	readonly earnings: number;
	/** Checkouts where the code was applied, booked or not. */
	readonly codeApplied: number;
	readonly bookings: number;
	readonly averageOrderValue: number;
	readonly couponsUsed: number;
	/** Earnings and bookings per week of the period, oldest first. */
	readonly weeks: readonly {
		readonly start: string;
		readonly earnings: number;
		readonly bookings: number;
	}[];
}

/** The Analytics tab: the period, then every booking and each type alone. */
export interface CreatorAnalytics {
	/** ISO dates, inclusive. */
	readonly period: { readonly from: string; readonly to: string };
	readonly all: AnalyticsBreakdown;
	readonly flights: AnalyticsBreakdown;
	readonly hotels: AnalyticsBreakdown;
}

/** Where a connected platform stands.
 *  - `in_review`: checked before the code goes live there (Instagram, YouTube,
 *    X, Snapchat).
 *  - `verification_pending`: WhatsApp / Telegram. The code already works, but
 *    payouts wait until ops verify the channel.
 *  - `verified`, `rejected`: the outcome. */
export type PlatformStatus =
	| 'in_review'
	| 'verification_pending'
	| 'verified'
	| 'rejected';

/** A social platform the creator shares their code on. */
export interface ConnectedPlatform {
	readonly id: string;
	readonly platform: CreatorPlatform;
	/** "Riya's Travel Deals", "@wanderwithriya", or the link while it has
	 *  neither. */
	readonly name: string;
	readonly status: PlatformStatus;
	/** ISO timestamps; `verifiedAt` stays null until it's verified. */
	readonly addedAt: string | null;
	readonly verifiedAt: string | null;
}
